'use client';

import { useEffect, useRef } from 'react';
import { useSiteSettings } from '@/components/providers';
import { useServiceConsentGranted } from '@/features/consent';
import { MATOMO_SERVICE_ID } from './MatomoTagManager';

declare global {
    interface Window {
        _mtm?: Array<Record<string, unknown>>;
    }
}

/**
 * Treats an intercepting-route modal ("/m/...") as a navigable page view.
 *
 * Modals open via client-side navigation, so neither the browser tab title nor
 * the Matomo container updates on its own. Rendered inside the modal with the
 * page's title, this component:
 *
 *   - switches `document.title` to the modal's title while it is open and restores
 *     the previous title when the modal closes, and
 *   - pushes a Matomo "mtm.PageView" with that title and the current URL.
 *
 * The page view reports the title passed in here instead of reading `document.title`,
 * so it is immune to the race between the navigation commit and the title update:
 * the correct title and location are always sent. Gated on the same Matomo
 * service consent as the container script (see MatomoTagManager).
 */
export function ModalPageView({ title }: { title: string }) {
    const settings = useSiteSettings();
    const matomo = settings.tracking?.matomoTagManager;
    const enabled = Boolean(matomo?.enabled && matomo?.containerUrl?.trim());
    const analyticsGranted = useServiceConsentGranted(MATOMO_SERVICE_ID);

    // Mirror the site-wide title template ("%s | <site title>") from the root layout
    // so the modal reports the same title format as every other page.
    const siteTitle = settings.seo?.title || settings.siteTitle || '';
    const documentTitle = title ? (siteTitle ? `${title} | ${siteTitle}` : title) : siteTitle;

    // Switch the tab title to the modal while it is open, restore it on close.
    useEffect(() => {
        if (!documentTitle) return;
        const previousTitle = document.title;
        document.title = documentTitle;
        return () => {
            document.title = previousTitle;
        };
    }, [documentTitle]);

    // Report the modal as a page view: once per distinct URL, and only with consent.
    const lastTrackedUrl = useRef<string | null>(null);
    useEffect(() => {
        if (!enabled || !analyticsGranted) return;

        const url = window.location.href;
        if (lastTrackedUrl.current === url) return;
        lastTrackedUrl.current = url;

        const dataLayer = (window._mtm ??= []);
        dataLayer.push({
            event: 'mtm.PageView',
            'page.url': url,
            'page.title': documentTitle,
        });
    }, [enabled, analyticsGranted, documentTitle]);

    return null;
}
